import { ApolloError, gql, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { OptionProps } from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ErrorAlert, WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { ZteEnrolmentStatus } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { useBulkPairZteEnrolmentToCustomerMutation } from './__generated__/PairdeviceModal.graphql';

const customerOption = selectOptionBaseSchema;
type CustomerOption = z.TypeOf<typeof customerOption>;

const FormData = z.object({
  customerId: customerOption.optional().nullable(),
});
type FormData = z.infer<typeof FormData>;

interface ZteEnrolment {
  id: string;
  status: ZteEnrolmentStatus;
  customerId?: string;
}

interface Customer {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (customerId: string) => Promise<void> | void;
  enrolmentIds: string[];
}

export function PairDevicesModal({ isOpen, onCancel, onSuccess, enrolmentIds }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <PairDevicesModalContent
          onCancel={onCancel}
          onSuccess={onSuccess}
          enrolmentIds={enrolmentIds}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  onCancel: () => void;
  onSuccess: (customerId: string) => Promise<void> | void;
  enrolmentIds: string[];
};

function PairDevicesModalContent({ onCancel, onSuccess, enrolmentIds }: ContentProps) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const isPairingAllowed = verifyUserPermissions([Permission.ZteAssignCustomer]);
  const [hasPairedDevices, setHasPairedDevices] = useState(false);

  const validatedEnrolmentIds = React.useMemo(() => {
    if (!enrolmentIds || !Array.isArray(enrolmentIds)) return [];
    return enrolmentIds.filter((id) => typeof id === 'string' && id.length > 0);
  }, [enrolmentIds]);

  const { data: customersData, error: customersError } = useQuery(
    PairDevicesModal.queries.PairDevicesCustomersPage,
  );

  const { data: enrolmentsData } = useQuery(PairDevicesModal.queries.EnrolmentsForOrganization, {
    variables: {
      pageSize: 100,
      pageNumber: 1,
    },
    skip: validatedEnrolmentIds.length === 0,
  });

  const customerOptions = React.useMemo(() => {
    const customers = customersData?.organization?.customers || [];
    return customers.map(
      (customer: Customer): CustomerOption => ({
        label: customer.name,
        value: customer.id,
      }),
    );
  }, [customersData]);

  useEffect(() => {
    if (enrolmentsData?.enrolmentsForOrganization?.zteEnrolment) {
      const selectedEnrolments = enrolmentsData.enrolmentsForOrganization.zteEnrolment.filter(
        (enrolment: ZteEnrolment) => validatedEnrolmentIds.includes(enrolment.id),
      );

      const hasPaired = selectedEnrolments.some(
        (enrolment: ZteEnrolment) => enrolment.status === ZteEnrolmentStatus.Paired,
      );
      setHasPairedDevices(hasPaired);
    }
  }, [enrolmentsData, validatedEnrolmentIds]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(FormData),
    defaultValues: {
      customerId: null,
    },
  });

  const [bulkPairZteEnrolmentToCustomer] = useBulkPairZteEnrolmentToCustomerMutation();

  //const customers: Customer[] = customersData?.organization?.customers || [];

  const onSubmit = useCallback(
    async ({ customerId }: FormData) => {
      try {
        if (validatedEnrolmentIds.length === 0) {
          throw new Error('No valid enrolment IDs provided');
        }

        if (!customerId?.value) {
          throw new Error('No customer selected');
        }

        const { data } = await bulkPairZteEnrolmentToCustomer({
          variables: {
            input: {
              customerId: customerId.value,
              enrolmentIds: validatedEnrolmentIds,
            },
          },
        });

        if (!data?.bulkPairZteEnrolmentToCustomer) {
          throw new Error('Pairing operation failed');
        }

        const enrolments = data.bulkPairZteEnrolmentToCustomer.enrolments || [];
        if (enrolments.length === 0) {
          throw new Error('No enrolments were paired');
        }

        // const customerName =
        //   customers.find((c) => c.id === customerId)?.name ?? t('selectedCustomer');

        toast({
          status: 'success',
          title: t('pairDevicesSuccess'),
          description: t('devicesSuccessfullyPaired', {
            count: enrolments.length,
            customerName: customerId.label,
          }),
        });

        await onSuccess?.(customerId.value);
      } catch (err) {
        const error = err as ApolloError;
        console.error('Error in onSubmit:', err);

        toast({
          status: 'error',
          title: t('pairDevicesError'),
          description: fromError(error, 'PairDevices', {
            ZTE_ENROLMENT_NOT_FOUND: t('EnrolmentNotFound'),
          }),
        });
      }
    },
    [toast, bulkPairZteEnrolmentToCustomer, onSuccess, validatedEnrolmentIds],
  );

  if (customersError) {
    return (
      <>
        <ModalHeader>{t('selectCustomer')}</ModalHeader>
        <ModalCloseButton onClick={onCancel} />
        <ModalBody>
          <ErrorAlert>{t('failedToLoadCustomers')}</ErrorAlert>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('close')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{t('selectCustomer')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack spacing={4} align="stretch">
          {!isPairingAllowed && <ErrorAlert>{t('youDoNotHavePermission')}</ErrorAlert>}

          {hasPairedDevices ? (
            <WarningAlert>{t('AlreadyPairedDevicesWarning')}</WarningAlert>
          ) : (
            <WarningAlert>{t('pairDevicesWarning')}</WarningAlert>
          )}

          <Text color={!isPairingAllowed || hasPairedDevices ? 'gray.500' : 'inherit'}>
            {t('selectCustomerToPair')}
          </Text>

          <FormControl isRequired isInvalid={Boolean(errors.customerId)}>
            <FormLabel>{t('selectCustomer')}</FormLabel>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  options={customerOptions}
                  components={{
                    ...components,
                    Option: PairDevicesSelectOption,
                  }}
                  onChange={field.onChange}
                  isLoading={isSubmitting}
                  placeholder={t('selectCustomerPlaceholder')}
                  isMulti={false}
                  escapeClearsValue={false}
                  isClearable={true}
                  isSearchable={true}
                  menuPlacement="auto"
                  isDisabled={
                    isSubmitting ||
                    validatedEnrolmentIds.length === 0 ||
                    !isPairingAllowed ||
                    hasPairedDevices
                  }
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="customerId"
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FormControl>

          {customerOptions.length === 0 && <WarningAlert>{t('noCustomersAvailable')}</WarningAlert>}
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={
            isSubmitting ||
            Object.keys(errors).length > 0 ||
            customerOptions.length === 0 ||
            validatedEnrolmentIds.length === 0 ||
            !isPairingAllowed ||
            hasPairedDevices
          }
          isLoading={isSubmitting}
        >
          {t('pairToCustomer')}
        </Button>
      </ModalFooter>
    </form>
  );
}

export function PairDevicesSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<CustomerOption, false>) {
  const data = rest.data as CustomerOption;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

PairDevicesModal.queries = {
  PairDevicesCustomersPage: gql`
    query PairDevicesCustomersPage {
      organization {
        id
        customers {
          id
          name
        }
      }
    }
  `,
  EnrolmentsForOrganization: gql`
    query EnrolmentsForOrganization($pageSize: Int!, $pageNumber: Int!) {
      enrolmentsForOrganization(pageSize: $pageSize, pageNumber: $pageNumber) {
        totalCount
        zteEnrolment {
          id
          status
          customerId
        }
      }
    }
  `,
};

PairDevicesModal.mutations = {
  bulkPairZteEnrolmentToCustomer: gql`
    mutation bulkPairZteEnrolmentToCustomer($input: ZteBulkEnrolmentPairInput!) {
      bulkPairZteEnrolmentToCustomer(input: $input) {
        enrolments {
          id
        }
      }
    }
  `,
};
